"use client";

import React, { useEffect, useState } from "react";
import { createTrade, createOrder } from "../services/digitalWalletService";
import { postBusinessTradingCreateTrade } from "../services/businessWalletService";
import { useToast } from "../hooks/use-toast";
import { QuoteConfirmationCard } from "./QuoteConfirmationCard";
import { TransactionDetailRow } from "./TransactionDetailRow";
import { Typography } from "./ui/typography";
import { truncateDecimals } from "../utils/numberFormatter";

interface QuoteData {
  incomingAmount?: number;
  outgoingAmount?: number;
  serviceFee?: number;
  feePercentage?: {
    $numberDecimal?: string;
  };
  tradeType?: string;
  pair?: string;
  userId?: string;
  rate?: number;
  assetId?: string;
  quoteId?: string;
  createdAt?: number;
  expiresAt?: number;
  [key: string]: unknown;
}

interface TradeData {
  incomingAmount?: number | null;
  [key: string]: unknown;
}

interface OrderData {
  targetPrice?: number | null;
  [key: string]: unknown;
}

interface QuoteDetailsProps {
  onBack: () => void;
  onConfirm: (result: any) => Promise<void>;
  data: QuoteData;
  tradeData?: TradeData;
  orderData?: OrderData;
  orderSaleData?: OrderData;
  tradeSellData?: TradeData;
  typeOfTrade: string;
  isOrder: boolean;
  userId: string;
  isPostTrade: boolean;
  businessId?: string;
  assetId?: string;
  saleAssetId?: string;
  rate?: number;
  onRefresh?: () => void | Promise<void>;
  assetIcon?: string;
  assetName?: string;
  assetSymbol?: string;
  dashboard?: boolean;
}

const QuoteDetails: React.FC<QuoteDetailsProps> = ({
  onBack,
  onConfirm,
  data,
  orderData,
  typeOfTrade,
  isOrder,
  userId,
  isPostTrade,
  businessId,
  assetId,
  saleAssetId,
  rate,
  onRefresh,
  assetIcon,
  assetName,
  assetSymbol,
  dashboard = false,
}) => {
  const feePercentage = 0;
  const outgoingAmount = data?.outgoingAmount ?? 0;
  const incomingAmount = data?.incomingAmount ?? 0;
  const currentRate = data?.rate || 0;
  const serviceFee = 0;
  const [asset, currency] = data?.pair?.split("-") || ["", ""];
  const selectedAsset = asset?.toUpperCase();
  const selectedCurrency = currency?.toUpperCase();
  const [timer, setTimer] = useState<number>(10);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState<boolean>(false);
  const { toast } = useToast();

  const getCurrencySymbol = (currencyCode: string): string => {
    const code = currencyCode?.toUpperCase();
    if (code === "ZAR") return "R";
    if (code === "USD") return "$";
    if (code === "EUR") return "€";
    if (code === "GBP") return "£";
    return code || "R";
  };

  const currencySymbol = getCurrencySymbol(selectedCurrency);

  const formatCrypto = (value: number, symbol: string): string => {
    const symbolUpper = symbol?.toUpperCase();
    const decimals = symbolUpper === "BTC" ? 6 : 2;
    return `${truncateDecimals(value, decimals)} ${symbol}`;
  };

  const formatFiat = (value: number, currencyCode?: string): string => {
    const symbol = getCurrencySymbol(currencyCode || selectedCurrency);
    return `${symbol} ${truncateDecimals(value, 2)}`;
  };

  const formattedServiceFee = formatCrypto(serviceFee, selectedAsset);

  const formattedIncomingAmount =
    typeOfTrade === "Buy"
      ? formatCrypto(incomingAmount, selectedAsset)
      : formatFiat(incomingAmount);
  const formattedOutgoingAmount =
    typeOfTrade === "Buy"
      ? formatFiat(outgoingAmount)
      : formatCrypto(outgoingAmount, selectedAsset);

  const parsedFiat = outgoingAmount || 0;
  const finalCrypto = currentRate > 0 ? parsedFiat / currentRate : 0;
  const serviceFeeCrypto = finalCrypto * feePercentage;
  const finalCryptoAfterFee = finalCrypto - serviceFeeCrypto;

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    setIsLoadingQuote(true);
    try {
      await onRefresh?.();
    } finally {
      setIsRefreshing(false);
      setIsLoadingQuote(false);
    }
  };
  useEffect(() => {
    if (data) {
      setTimer(10);
      setIsRefreshing(false);
      setIsLoadingQuote(false);
    }
  }, [data]);

  const isTimerZero = timer === 0;

  const handleConfirm = (): void => {
    if (!outgoingAmount || !selectedAsset || !selectedCurrency) {
      alert("Order details are incomplete.");
      return;
    }
    setConfirmed(true);
    setTimer(0);
    makeTrade();
  };

  const handleLocalBack = (): void => {
    onBack();
  };

  const tradingData = {
    quoteId: data?.quoteId,
    brokerPercentage: 0,
    brokerAmount: 0,
  };

  const customeOrderData = {
    userId: userId,
    currencyPair: data?.pair,
    incomingAsset: typeOfTrade === "Buy" ? asset : currency,
    outgoingAsset: typeOfTrade === "Buy" ? currency : asset,
    orderType: typeOfTrade === "Buy" ? "below" : "above",
    type: typeOfTrade?.toLowerCase(),
    price: rate,
    targetPrice: data?.rate,
    incomingAmount: data?.incomingAmount,
    outgoingAmount: data?.outgoingAmount,
    serviceFee: 0,
    brokerPercentage: 0,
    note: "",
    isPostTrade: isPostTrade,
    assetId: typeOfTrade === "Buy" ? assetId : saleAssetId,
  };

  const makeTradeWithOrder = async (): Promise<void> => {
    try {
      let result;
      if (typeOfTrade === "Buy") {
        result = await createOrder(customeOrderData);
      } else if (typeOfTrade === "Sell") {
        result = await createOrder(customeOrderData);
      }

      const isSuccess = result?.data && result?.status === 200;

      await onConfirm({
        success: isSuccess,
        data: result?.data,
        message: isSuccess
          ? "Order created successfully"
          : "Order creation failed",
        originalResult: result,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      await onConfirm({
        success: false,
        error: errorMessage,
        message: "Order creation failed due to an error",
      });
    }
  };

  const makeTradeWithTrade = async (): Promise<void> => {
    try {
      let result;
      if (businessId) {
        result = await postBusinessTradingCreateTrade(businessId, {
          quoteId: data?.quoteId,
          brokerPercentage: 0,
          brokerAmount: 0,
          autoStack: false,
        });
      } else if (typeOfTrade === "Buy") {
        result = await createTrade(tradingData);
      } else if (typeOfTrade === "Sell") {
        result = await createTrade(tradingData);
      }

      const responseData = result?.data;
      const isSuccess =
        result?.status === 200 &&
        responseData?.status === true &&
        responseData?.data?.trade !== undefined;

      await onConfirm({
        success: isSuccess,
        data: responseData?.data || responseData,
        trade: responseData?.data?.trade,
        message: isSuccess
          ? responseData?.message || "Trade completed successfully"
          : responseData?.message || "Trade failed",
        originalResult: result,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      await onConfirm({
        success: false,
        error: errorMessage,
        message: "Trade failed due to an error",
      });
    }
  };

  const makeTrade = (): void => {
    try {
      if (isOrder) {
        void makeTradeWithOrder();
      }
      if (!isOrder) {
        void makeTradeWithTrade();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      toast({
        description: errorMessage,
        variant: "error",
      });
    }
  };

  return (
    <>
      {isOrder === false && (
        <QuoteConfirmationCard
          title={
            typeOfTrade === "Buy" ? "Confirm Purchase" : "Sell Confirmation"
          }
          headerLabel={
            typeOfTrade === "Buy" ? "You are buying" : "You are selling"
          }
          assetSymbol={assetSymbol}
          assetName={assetName}
          assetIcon={assetIcon}
          amount={`${selectedAsset} ${
            typeOfTrade === "Buy"
              ? truncateDecimals(incomingAmount, 6)
              : truncateDecimals(outgoingAmount, 6)
          }`}
          fiatAmount={
            typeOfTrade === "Buy"
              ? truncateDecimals(outgoingAmount, 0)
              : truncateDecimals(incomingAmount, 0)
          }
          timer={timer}
          isTimerZero={isTimerZero}
          confirmed={confirmed}
          isLoadingQuote={isLoadingQuote}
          isRefreshing={isRefreshing}
          confirmButtonText={
            dashboard
              ? "Confirm"
              : typeOfTrade === "Buy"
                ? "Confirm Buy"
                : "Confirm Sell"
          }
          onBack={handleLocalBack}
          onRefresh={handleRefresh}
          onConfirm={handleConfirm}
          dashboard={dashboard}
        >
          <TransactionDetailRow
            label={typeOfTrade === "Buy" ? "Buying with" : "Sell for"}
            value={selectedCurrency}
          />
          {typeOfTrade === "Buy" && (
            <TransactionDetailRow
              label="Amount"
              value={formattedOutgoingAmount}
            />
          )}
          {typeOfTrade === "Sell" && (
            <TransactionDetailRow
              label="You receive"
              value={formattedIncomingAmount}
            />
          )}
          <TransactionDetailRow
            label="Exchange rate"
            value={`1 ${selectedAsset} = ${currencySymbol} ${truncateDecimals(currentRate, 2)}`}
          />
          <TransactionDetailRow
            label="Service charge"
            value={
              <div className="flex flex-col items-end gap-1">
                <Typography>{formattedServiceFee}</Typography>
                <Typography className="text-xs text-text-secondary">
                  0%
                </Typography>
              </div>
            }
          />
          <TransactionDetailRow
            label={typeOfTrade === "Buy" ? "Total cost" : "Total received"}
            value={
              typeOfTrade === "Buy"
                ? formattedOutgoingAmount
                : formattedIncomingAmount
            }
            isBold={true}
            valueClassName="text-text-primary font-semibold"
          />
        </QuoteConfirmationCard>
      )}
      {typeOfTrade === "Buy" && isOrder === true && (
        <QuoteConfirmationCard
          amount={`${selectedAsset} ${formatCrypto(finalCryptoAfterFee, selectedAsset)}`}
          fiatAmount={truncateDecimals(parsedFiat, 0)}
          timer={timer}
          isTimerZero={isTimerZero}
          confirmed={confirmed}
          isLoadingQuote={isLoadingQuote}
          isRefreshing={isRefreshing}
          confirmButtonText={dashboard ? "Confirm" : "Confirm Buy"}
          onBack={handleLocalBack}
          onRefresh={handleRefresh}
          onConfirm={handleConfirm}
          dashboard={dashboard}
        >
          <TransactionDetailRow label="Pay with" value={selectedCurrency} />
          <TransactionDetailRow label="Deposit to" value="Digital Wallet" />
          <TransactionDetailRow
            label="You receive"
            value={formatCrypto(finalCryptoAfterFee, selectedAsset)}
          />
          <TransactionDetailRow
            label={`Current ${selectedAsset} price`}
            value={`${currencySymbol} ${truncateDecimals(rate || 0, 2)}`}
          />
          <TransactionDetailRow
            label={`Target ${selectedAsset} price`}
            value={`${currencySymbol} ${orderData?.targetPrice}`}
          />
          <TransactionDetailRow
            label="Service charge"
            showInfo={true}
            value={
              <div className="flex flex-col items-end gap-1">
                <Typography>{formattedServiceFee}</Typography>
                <Typography className="text-xs text-text-secondary">
                  0%
                </Typography>
              </div>
            }
          />
          <TransactionDetailRow
            label="Total cost"
            value={formatFiat(parsedFiat)}
            isBold={true}
          />
        </QuoteConfirmationCard>
      )}
      {typeOfTrade === "Sell" && isOrder === true && (
        <QuoteConfirmationCard
          amount={`${selectedAsset} ${formatCrypto(outgoingAmount, selectedAsset)}`}
          assetSymbol={assetSymbol}
          fiatAmount={truncateDecimals(incomingAmount, 0)}
          timer={timer}
          isTimerZero={isTimerZero}
          confirmed={confirmed}
          isLoadingQuote={isLoadingQuote}
          isRefreshing={isRefreshing}
          confirmButtonText={dashboard ? "Confirm" : "Confirm Sell"}
          onBack={handleLocalBack}
          onRefresh={handleRefresh}
          onConfirm={handleConfirm}
          dashboard={dashboard}
        >
          <TransactionDetailRow label="Sell for" value={selectedCurrency} />
          <TransactionDetailRow label="Deposit to" value="Digital Wallet" />
          <TransactionDetailRow
            label="You receive"
            value={formattedIncomingAmount}
          />
          <TransactionDetailRow
            label={`Current ${selectedAsset} price`}
            value={`${currencySymbol} ${truncateDecimals(rate || 0, 2)}`}
          />
          <TransactionDetailRow
            label={`Target ${selectedAsset} price`}
            value={`${currencySymbol} ${truncateDecimals(currentRate, 2)}`}
          />
          <TransactionDetailRow
            label="Service charge"
            showInfo={true}
            value={
              <div className="flex flex-col items-end gap-1">
                <Typography>{formattedServiceFee}</Typography>
                <Typography className="text-xs text-text-secondary">
                  0%
                </Typography>
              </div>
            }
          />
          <TransactionDetailRow
            label="Total received"
            value={formattedIncomingAmount}
            isBold={true}
          />
        </QuoteConfirmationCard>
      )}
    </>
  );
};

export default QuoteDetails;
